/**
 * Extract Utarus report / BinDrive view URLs from free text.
 *
 * Task results and notifications often cite external news sources. The Tasks
 * UI must not label the first arbitrary https://… as "Open report".
 *
 * Accepted shapes (see report/publish.ts dual delivery):
 *   - permanent public:  {origin}/reports/<owner>-<file>.html
 *   - signed BinDrive:   {origin}/api/files/<file>.html/view?slug=…&t=…
 */

const URL_RE = /https?:\/\/[^\s)\]>"']+/gi;

function stripTrailingPunct(raw: string): string {
  return raw.replace(/[.,;:]+$/, '');
}

/** Permanent public HTML report under /reports/. */
export function isPublicReportUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    return /^\/reports\/[^/]+\.html$/i.test(u.pathname);
  } catch {
    return false;
  }
}

/** Signed (or owner-scoped) BinDrive HTML view path. */
export function isBinDriveViewUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    return /^\/api\/files\/[^/]+\.html\/view$/i.test(u.pathname);
  } catch {
    return false;
  }
}

export function isReportUrl(raw: string): boolean {
  return isPublicReportUrl(raw) || isBinDriveViewUrl(raw);
}

/**
 * First report-like URL in `text`, preferring permanent /reports/ links over
 * signed /api/files/…/view links. Returns null when none qualify (e.g. only
 * news citations like devops.com).
 */
export function firstReportUrl(text: string): string | null {
  if (!text) return null;
  const candidates: string[] = [];
  for (const m of text.matchAll(URL_RE)) {
    candidates.push(stripTrailingPunct(m[0]));
  }
  for (const url of candidates) {
    if (isPublicReportUrl(url)) return url;
  }
  for (const url of candidates) {
    if (isBinDriveViewUrl(url)) return url;
  }
  return null;
}
